import { DateInput, RangeCalendar, RangeValue, TimeInput } from '@heroui/react';
import { CalendarDateTime, isSameDay } from '@internationalized/date';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import CalendarIcon from '@shared/assets/icons/v2/calendar.svg';
import { View } from '@shared/components/View';
import { toISOString, toLocalCalendarDateTime } from '@shared/util/date';
import DateInputStyles from './DateInput.module.css';
import styles from './DateRangePicker.module.css';
import RangeCalendarStyles from './RangeCalendar.module.css';
import { TimeClock } from './TimeClock';

export type DateRangeValue = RangeValue<CalendarDateTime>;

interface DateRangePickerProps {
  startDateStamp: string | undefined;
  endDateStamp: string | undefined;
  setStartDateStamp: Dispatch<SetStateAction<string | undefined>>;
  setEndDateStamp: Dispatch<SetStateAction<string | undefined>>;
}

export function DateRangePicker({
  startDateStamp,
  endDateStamp,
  setStartDateStamp,
  setEndDateStamp,
}: DateRangePickerProps) {
  const [startDate, setStartDate] = useState(getCalendarDateTime(startDateStamp));
  const [startTime, setStartTime] = useState(startDate);
  const [endDate, setEndDate] = useState(getCalendarDateTime(endDateStamp));
  const [endTime, setEndTime] = useState(endDate);
  const [initialValue, setInitialValue] = useState(() => getDateRange({ startDate, endDate }));
  const [dateRange, setDateRange] = useState(initialValue);

  useEffect(() => {
    // if they add a date that was null, set the associated time
    const today = toLocalCalendarDateTime(new Date().toISOString());
    if (startDate && !startTime) {
      setStartTime(today.set({ hour: 9, minute: 0 }));
    }
    if (endDate && !endTime) {
      setEndTime(today.set({ hour: 17, minute: 0 }));
    }
  }, [startDate, endDate, startTime, endTime]);

  useEffect(() => {
    // update the date range when the start/end dates change
    if (startDate && endDate) {
      setDateRange(prev => {
        const updatedRange = getDateRange({ startDate, endDate });
        if (isEqual(prev, updatedRange)) {
          return prev;
        } else {
          return updatedRange;
        }
      });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    // update the start/end dates when the date range changes
    if (dateRange && dateRange !== initialValue) {
      setStartDate(prev => {
        if (isEqual(prev, dateRange.start)) {
          return prev;
        } else {
          return dateRange.start;
        }
      });
      setEndDate(prev => {
        if (isEqual(prev, dateRange.end)) {
          return prev;
        } else {
          return dateRange.end;
        }
      });
      setInitialValue(dateRange);
    }
  }, [dateRange, initialValue]);

  useEffect(() => {
    // update the external values when the start/end dates change
    const updatedStart = getUpdatedDateTime(startDate, startTime);
    const updatedEnd = getUpdatedDateTime(endDate, endTime);
    setStartDateStamp(updatedStart);
    setEndDateStamp(updatedEnd);
  }, [startDate, endDate, startTime, endTime, setStartDateStamp, setEndDateStamp]);

  const onBlurDate = useCallback(() => {
    // when the blur a date field, ensure the start date is before the end date
    if (startDate && endDate && startDate > endDate) {
      // swap only the date components, preserving the original time components
      const { year: startYear, month: startMonth, day: startDay } = startDate;
      const { year: endYear, month: endMonth, day: endDay } = endDate;
      setStartDate(startDate.set({ year: endYear, month: endMonth, day: endDay }));
      setEndDate(endDate.set({ year: startYear, month: startMonth, day: startDay }));
    }
  }, [startDate, endDate]);

  const onBlurTime = useCallback(() => {
    // when blurring a time field, ensure start time is before end time
    if (startDate && endDate && isSameDay(startDate, endDate) && startDate > endDate) {
      // swap only the time components, preserving the original date components
      const { hour: startHour, minute: startMinute } = startDate;
      const { hour: endHour, minute: endMinute } = endDate;
      setStartDate(startDate.set({ hour: endHour, minute: endMinute }));
      setEndDate(endDate.set({ hour: startHour, minute: startMinute }));
    }
  }, [startDate, endDate]);

  const onChangeStartTime = useOnChangeTime(setStartTime);
  const onChangeEndTime = useOnChangeTime(setEndTime);

  return (
    <View className={styles.dateRangePicker}>
      <DateInput
        label="Start Date"
        labelPlacement="outside"
        granularity="day"
        value={startDate}
        onChange={setStartDate}
        onBlur={onBlurDate}
        maxValue={endDate}
        classNames={DateInputStyles}
        startContent={<CalendarIcon className={styles.calendarIcon} />}
      />
      <TimeInput
        label="Start Time"
        labelPlacement="outside"
        classNames={DateInputStyles}
        value={startTime}
        onChange={onChangeStartTime}
        onBlur={onBlurTime}
        startContent={<TimeClock time={startTime} className={styles.clockIcon} />}
      />
      <DateInput
        label="End Date"
        labelPlacement="outside"
        granularity="day"
        value={endDate}
        onChange={setEndDate}
        onBlur={onBlurDate}
        minValue={startDate}
        classNames={DateInputStyles}
        startContent={<CalendarIcon className={styles.calendarIcon} />}
      />
      <TimeInput
        label="End Time"
        labelPlacement="outside"
        classNames={DateInputStyles}
        value={endTime}
        onChange={onChangeEndTime}
        onBlur={onBlurTime}
        startContent={<TimeClock time={endTime} className={styles.clockIcon} />}
      />
      <RangeCalendar
        classNames={RangeCalendarStyles}
        calendarWidth="100%"
        showMonthAndYearPickers={true}
        value={dateRange}
        onChange={setDateRange}
      />
    </View>
  );
}

export function getDefaultDateRangeValue(): DateRangeValue {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  return {
    start: new CalendarDateTime(year, month, day, 9, 0),
    end: new CalendarDateTime(year, month, day, 17, 0),
  };
}

function useOnChangeTime(setTime: Dispatch<SetStateAction<CalendarDateTime | null>>) {
  // By default, HeroUI's TimeInput doesn't handle rollover. So this wraps the
  // setTime function to handle rolling over from 11:59pm to 12:00am, etc.
  return useCallback(
    (time: CalendarDateTime | null) => {
      setTime(prev => handleRollover(prev, time));
    },
    [setTime],
  );
}

export function handleRollover(
  prev: CalendarDateTime | null,
  next: CalendarDateTime | null,
): CalendarDateTime | null {
  if (!next) {
    return prev;
  }
  let updatedTime = next;
  if (prev?.minute === 59 && next.minute === 0) {
    updatedTime = next.set({ hour: prev.hour + 1 });
  }
  if (prev && next && prev.minute === 0 && next.minute === 59) {
    updatedTime = next.set({ hour: prev.hour - 1 });
  }
  if (prev?.hour === 11 && next.hour === 0) {
    updatedTime = next.set({ hour: 12 });
  }
  if (prev?.hour === 12 && next.hour === 23) {
    updatedTime = next.set({ hour: 11 });
  }
  if (prev?.hour === 0 && next.hour === 11) {
    updatedTime = next.set({ hour: 23 });
  }
  if (prev?.hour === 23 && next.hour === 12) {
    updatedTime = next.set({ hour: 0 });
  }
  return updatedTime;
}

function getDateRange({
  startDate,
  endDate,
}: {
  startDate: CalendarDateTime | null;
  endDate: CalendarDateTime | null;
}): DateRangeValue | null {
  if (startDate && endDate) {
    return { start: startDate, end: endDate };
  }
  const date = startDate || endDate;
  if (date) {
    return { start: date, end: date };
  }
  return null;
}

function getCalendarDateTime(dateStamp: string | undefined): CalendarDateTime | null {
  return dateStamp ? toLocalCalendarDateTime(dateStamp) : null;
}

function getUpdatedDateTime(
  date: CalendarDateTime | null,
  time: CalendarDateTime | null,
): string | undefined {
  if (!date || !time) {
    return undefined;
  }
  const updatedDate = new CalendarDateTime(date.year, date.month, date.day, time.hour, time.minute);
  return toISOString(updatedDate);
}
